import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2, Mail, Sparkles } from "lucide-react";
import { useMatch } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

/**
 * Floating AI support chat widget.
 *
 * Bottom-right bubble → opens a panel with conversation history.
 * First message posts to the support-chat edge function (Claude Haiku).
 * If the AI escalates (or the user clicks "Email support"), the full
 * transcript is forwarded to the support team via email and the UI
 * tells the user they'll hear back within 24h.
 *
 * State persisted in localStorage:
 *   fcb_support_visitor_id    — stable per-browser anonymous ID
 *   fcb_support_conversation  — active conversation ID (resumed on reload)
 *   fcb_support_open          — whether the panel was open when last used
 */

const LS_VISITOR = "fcb_support_visitor_id";
const LS_CONVO = "fcb_support_conversation";

interface Message {
  role: "user" | "assistant";
  content: string;
  at?: string;
}

// ── Minimal safe markdown renderer for chat bubbles ──
// Handles: **bold**, *italic*, `code`, [text](url), bulleted (-, *) and
// numbered (1.) lists, paragraphs (double newline), line breaks.
// Escapes HTML first so nothing the model returns can execute.
function renderInline(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let i = 0;
  let key = 0;
  while (i < text.length) {
    // Bold **x**
    const bold = text.slice(i).match(/^\*\*([^*\n]+?)\*\*/);
    if (bold) {
      nodes.push(<strong key={key++}>{bold[1]}</strong>);
      i += bold[0].length;
      continue;
    }
    // Italic *x*
    const italic = text.slice(i).match(/^\*([^*\n]+?)\*/);
    if (italic) {
      nodes.push(<em key={key++}>{italic[1]}</em>);
      i += italic[0].length;
      continue;
    }
    // Inline code `x`
    const code = text.slice(i).match(/^`([^`\n]+?)`/);
    if (code) {
      nodes.push(
        <code key={key++} className="px-1 py-0.5 rounded bg-black/40 text-[0.9em] font-mono">
          {code[1]}
        </code>
      );
      i += code[0].length;
      continue;
    }
    // Link [text](url) — only http(s) / relative paths, no javascript:
    const link = text.slice(i).match(/^\[([^\]]+?)\]\((https?:\/\/[^\s)]+|\/[^\s)]*)\)/);
    if (link) {
      nodes.push(
        <a
          key={key++}
          href={link[2]}
          target={link[2].startsWith("http") ? "_blank" : undefined}
          rel="noopener noreferrer"
          className="text-primary underline hover:no-underline"
        >
          {link[1]}
        </a>
      );
      i += link[0].length;
      continue;
    }
    // Grab text up to next markdown token
    const next = text.slice(i).search(/[\*`\[]/);
    const chunk = next === -1 ? text.slice(i) : text.slice(i, i + Math.max(next, 1));
    nodes.push(<span key={key++}>{chunk}</span>);
    i += chunk.length;
  }
  return nodes;
}

function Markdown({ children }: { children: string }) {
  // Split into blocks on blank lines
  const blocks = children.split(/\n\n+/);
  return (
    <>
      {blocks.map((block, bi) => {
        const lines = block.split("\n");
        const isBulleted = lines.every((l) => /^\s*[-*]\s+/.test(l));
        const isNumbered = lines.every((l) => /^\s*\d+\.\s+/.test(l));

        if (isBulleted) {
          return (
            <ul key={bi} className="list-disc pl-5 my-1 space-y-1">
              {lines.map((l, li) => (
                <li key={li}>{renderInline(l.replace(/^\s*[-*]\s+/, ""))}</li>
              ))}
            </ul>
          );
        }
        if (isNumbered) {
          return (
            <ol key={bi} className="list-decimal pl-5 my-1 space-y-1">
              {lines.map((l, li) => (
                <li key={li}>{renderInline(l.replace(/^\s*\d+\.\s+/, ""))}</li>
              ))}
            </ol>
          );
        }
        // Regular paragraph — preserve single-line breaks
        return (
          <p key={bi} className={bi === 0 ? "" : "mt-2"}>
            {lines.map((l, li) => (
              <span key={li}>
                {renderInline(l)}
                {li < lines.length - 1 && <br />}
              </span>
            ))}
          </p>
        );
      })}
    </>
  );
}

function getVisitorId(): string {
  try {
    let id = localStorage.getItem(LS_VISITOR);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(LS_VISITOR, id);
    }
    return id;
  } catch {
    return "anonymous-" + Math.random().toString(36).slice(2);
  }
}

export function SupportChat() {
  const { user } = useAuth();
  // Hide the floating support bubble on group-chat pages — the page has
  // its own message composer and the bubble was overlapping the Send
  // button on mobile. Covers both the bare and tournament-scoped routes.
  const onGroupChatPage =
    !!useMatch("/groups/:groupId") || !!useMatch("/tournaments/:slug/groups/:groupId");
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [escalated, setEscalated] = useState(false);
  const [emailForm, setEmailForm] = useState(""); // for anonymous users before escalation
  const scrollRef = useRef<HTMLDivElement>(null);

  const conversationIdRef = useRef<string | null>(null);

  // Load saved conversation on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(LS_CONVO);
      if (saved) conversationIdRef.current = saved;
    } catch { /* no-op */ }
  }, []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (!open) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, open, sending]);

  const send = async (text: string, explicitEscalate = false) => {
    const trimmed = text.trim();
    if (!trimmed && !explicitEscalate) return;
    setSending(true);

    // Optimistic append of the user message
    if (trimmed) {
      setMessages((prev) => [...prev, { role: "user", content: trimmed }]);
    }
    setInput("");

    try {
      const baseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      const visitorId = getVisitorId();
      // Supabase's edge gateway wants BOTH the apikey header AND an
      // Authorization bearer. For logged-in users we send their JWT so
      // the function can load their user context; for anonymous visitors
      // we send the anon key as the bearer.
      const { data: { session } } = await supabase.auth.getSession();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        apikey: anonKey,
        Authorization: `Bearer ${session?.access_token || anonKey}`,
      };

      const resp = await fetch(`${baseUrl}/functions/v1/support-chat`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          conversationId: conversationIdRef.current,
          visitorId,
          message: trimmed || "(requested human support)",
          userEmail: user?.email || emailForm || undefined,
          escalate: explicitEscalate,
        }),
      });

      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.error || `HTTP ${resp.status}`);

      conversationIdRef.current = data.conversationId;
      try { localStorage.setItem(LS_CONVO, data.conversationId); } catch { /* no-op */ }

      setMessages((prev) => [...prev, { role: "assistant", content: data.reply }]);
      if (data.escalated) setEscalated(true);
    } catch (e: any) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Hmm — I'm having trouble connecting right now. Try again in a moment or email hello@fcbracket.com." },
      ]);
    } finally {
      setSending(false);
    }
  };

  const handleEscalate = () => {
    // If anonymous user and no email captured, prompt for it first
    if (!user && !emailForm) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "No problem — drop your email below and I'll route this to a human." },
      ]);
      return;
    }
    void send("", true);
  };

  const reset = () => {
    setMessages([]);
    setEscalated(false);
    setEmailForm("");
    conversationIdRef.current = null;
    try { localStorage.removeItem(LS_CONVO); } catch { /* no-op */ }
  };

  // Don't render the bubble at all on group-chat pages.
  if (onGroupChatPage) return null;

  // ── Closed bubble ─────────────────────────────────────────────
  if (!open) {
    return (
      <button
        onClick={() => {
          setOpen(true);
          if (messages.length === 0) {
            setMessages([
              {
                role: "assistant",
                content: "Hey — I'm Footy, the FC·Bracket support assistant. Ask me anything about pricing, brackets, prizes, or your account. If I can't help, I'll route you to a human.",
              },
            ]);
          }
        }}
        aria-label="Open support chat"
        className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-primary text-black flex items-center justify-center shadow-lg hover:scale-105 transition-transform"
      >
        <MessageCircle className="w-6 h-6" strokeWidth={2.5} />
      </button>
    );
  }

  // ── Open panel ────────────────────────────────────────────────
  return (
    <div className="fixed bottom-5 right-5 z-50 w-[min(380px,calc(100vw-24px))] h-[min(560px,calc(100vh-40px))] flex flex-col bg-black border border-border shadow-2xl rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-border bg-black">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center">
            <Sparkles className="w-3.5 h-3.5 text-primary" />
          </div>
          <div>
            <p className="font-display text-sm font-bold text-foreground leading-none">
              Footy <span className="text-muted-foreground font-normal text-[11px]">· FC·Bracket assistant</span>
            </p>
            <p className="text-[10px] text-muted-foreground mt-0.5">AI-powered · usually replies instantly</p>
          </div>
        </div>
        <button
          onClick={() => setOpen(false)}
          aria-label="Close chat"
          className="w-8 h-8 rounded-lg hover:bg-secondary flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                m.role === "user"
                  ? "bg-primary text-black rounded-br-sm whitespace-pre-wrap"
                  : "bg-secondary text-foreground rounded-bl-sm"
              }`}
            >
              {m.role === "assistant" ? <Markdown>{m.content}</Markdown> : m.content}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="px-3 py-2 rounded-2xl rounded-bl-sm bg-secondary text-muted-foreground text-sm flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Thinking…
            </div>
          </div>
        )}
      </div>

      {/* Escalated-state footer */}
      {escalated && (
        <div className="border-t border-border p-3 bg-primary/5">
          <p className="text-xs text-foreground font-semibold mb-1">📬 Routed to a human</p>
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            A team member will reply by email within 24 hours. Want to chat about something else?{" "}
            <button onClick={reset} className="text-primary underline hover:no-underline">Start over</button>.
          </p>
        </div>
      )}

      {/* Composer + email capture (anonymous) */}
      {!escalated && (
        <div className="border-t border-border p-3 space-y-2">
          {!user && (
            <input
              type="email"
              value={emailForm}
              onChange={(e) => setEmailForm(e.target.value)}
              placeholder="Your email (so we can reply if escalated)"
              className="w-full bg-secondary border border-border rounded-lg px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50"
              disabled={sending}
            />
          )}
          <div className="flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void send(input);
                }
              }}
              placeholder="Type a question…"
              rows={1}
              className="flex-1 bg-secondary border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 resize-none max-h-28"
              disabled={sending}
            />
            <button
              onClick={() => void send(input)}
              disabled={sending || !input.trim()}
              aria-label="Send message"
              className="w-9 h-9 rounded-lg bg-primary text-black flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:bg-primary/90 transition-colors shrink-0"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
          <button
            onClick={handleEscalate}
            disabled={sending}
            className="w-full text-center text-[11px] text-muted-foreground hover:text-primary transition-colors py-1 font-display tracking-wider flex items-center justify-center gap-1.5"
          >
            <Mail className="w-3 h-3" />
            EMAIL SUPPORT INSTEAD
          </button>
        </div>
      )}
    </div>
  );
}
